// @spec POL-STR-015
// @spec POL-STR-017
'use client';

import { useAuth } from '@clerk/clerk-react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  fetchPolicyMappings,
  updatePolicyMappings,
} from '@/features/policies/api/compliance-mappings';
import { useUpdatePolicy } from '@/features/policies/api/mutations';
import { usePolicyCategories } from '@/features/policies/api/policy-categories';
import { useFrameworks, usePolicy } from '@/features/policies/api/use-policies';
import { PolicyForm, type PolicyFormValues } from '@/features/policies/components/PolicyForm';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/policies/$policyId/edit')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.editPolicy'),
  }),
  component: EditPolicyPage,
});

/**
 * Edit Policy page - Form for editing existing policies.
 *
 * Features:
 * - Pre-populated form with existing policy data
 * - Rich text editor for content
 * - Category and framework selection
 * - Creates new draft version on save
 */
function EditPolicyPage() {
  const { t } = useTranslation('policies');
  const navigate = useNavigate();
  const { policyId } = Route.useParams();
  const { getToken, isSignedIn } = useAuth();

  // Data queries
  const { policy, isLoading: isPolicyLoading, isError, error } = usePolicy({ policyId });
  const { data: categoriesData, isLoading: isCategoriesLoading } = usePolicyCategories();
  const { data: frameworksData, isLoading: isFrameworksLoading } = useFrameworks();

  // Fetch existing compliance mappings for this policy
  const { data: existingMappings, isLoading: isMappingsLoading } = useQuery({
    queryKey: ['policy-mappings', policyId],
    queryFn: () => fetchPolicyMappings(getToken, policyId),
    enabled: (isSignedIn ?? false) && !!policyId,
    staleTime: 30 * 1000,
  });

  // Update mutation
  const updatePolicy = useUpdatePolicy();

  // Build default values from existing policy
  const defaultValues: PolicyFormValues | undefined = React.useMemo(() => {
    if (!policy) return undefined;

    return {
      title: policy.title,
      description: policy.description ?? '',
      type: policy.type,
      categoryId: policy.category?.id ?? '',
      content: policy.content ?? '',
      effectiveDate: policy.effectiveDate?.split('T')[0] ?? '',
      reviewFrequency: policy.reviewFrequency,
      frameworkIds: policy.complianceFrameworks.map((fw) => fw.id),
      tags: policy.tags,
    };
  }, [policy]);

  const isLoading = isPolicyLoading || isCategoriesLoading || isFrameworksLoading || isMappingsLoading;

  const handleSubmit = async (data: PolicyFormValues) => {
    try {
      await updatePolicy.mutateAsync({
        policyId,
        data: {
          title: data.title,
          description: data.description || null,
          categoryId: data.categoryId || null,
          content: data.content || null,
          effectiveDate: data.effectiveDate || null,
          reviewFrequency: data.reviewFrequency,
          frameworkIds: data.frameworkIds,
          tags: data.tags,
          // Change notes would come from a modal or additional field
          // For MVP, we'll include a generic note
          changeNotes: 'Policy updated via edit form',
        },
      });

      // Save compliance mappings if any were provided
      if (data.complianceMappings && data.complianceMappings.length > 0) {
        try {
          await updatePolicyMappings(getToken, policyId, data.complianceMappings);
        } catch (mappingErr) {
          const mappingMessage =
            mappingErr instanceof Error ? mappingErr.message : 'Failed to save compliance mappings';
          toast.warning(t('toast.policyUpdatedMappingsFailed'), {
            description: mappingMessage,
          });
        }
      }

      toast.success(t('policies:toast.policyUpdatedSuccessfully'), {
        description: t('toast.policyUpdatedDescription', { title: data.title }),
      });

      void navigate({
        to: '/policies/$policyId',
        params: { policyId },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      toast.error(t('policies:toast.failedToUpdatePolicy'), {
        description: message,
      });
    }
  };

  const handleCancel = () => {
    void navigate({
      to: '/policies/$policyId',
      params: { policyId },
    });
  };

  // Error state
  if (isError) {
    return (
      <div className="container max-w-4xl py-6">
        <div className="mb-4">
          <Button variant="ghost" size="sm" asChild className="-ml-2">
            <Link to="/policies/$policyId" params={{ policyId }}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('editPage.backToPolicy')}
            </Link>
          </Button>
        </div>
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6">
          <h2 className="text-lg font-semibold text-destructive">{t('editPage.errorLoadingTitle')}</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {error?.message ?? t('editPage.errorLoadingDescription')}
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => void navigate({ to: '/policies' })}
          >
            {t('editPage.returnToPolicyList')}
          </Button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading || !defaultValues) {
    return (
      <div className="container max-w-4xl py-6">
        <div className="mb-6 space-y-2">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-6 w-64" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const categories = categoriesData?.items ?? [];
  const frameworks = frameworksData ?? [];

  return (
    <div className="container max-w-4xl py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="mb-4">
          <Button variant="ghost" size="sm" asChild className="-ml-2">
            <Link to="/policies/$policyId" params={{ policyId }}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('editPage.backToPolicy')}
            </Link>
          </Button>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">{t('editPage.title')}</h1>
        <p className="text-muted-foreground">
          {t('editPage.description')}
        </p>
      </div>

      {/* Form */}
      <div className="rounded-lg border bg-card p-6">
        <PolicyForm
          defaultValues={defaultValues}
          categories={categories}
          frameworks={frameworks}
          existingMappings={existingMappings}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={updatePolicy.isPending}
          mode="edit"
        />
      </div>
    </div>
  );
}
